<template>
  <div
    class="file-dropzone"
    :class="{
      'is-dragover': isDragOver,
      'is-disabled': disabled,
      'file-dropzone-compact': compact,
      'has-file': Boolean(displayFileName)
    }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <!-- Hidden native file input overlay covering dropzone for seamless native click & drop -->
    <input
      type="file"
      ref="fileInput"
      :id="inputId"
      class="file-dropzone-input"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      @change="onNativeChange"
    />

    <!-- Selected File Presentation -->
    <div v-if="displayFileName" class="file-dropzone-selected">
      <div class="selected-info">
        <i :class="['selected-icon', computedFileIcon]"></i>
        <div class="selected-meta">
          <p class="selected-name" :title="displayFileName">{{ displayFileName }}</p>
          <span class="selected-size" v-if="displayFileSize">{{ displayFileSize }}</span>
          <span class="selected-size text-success" v-else>
            <i class="fa fa-check-circle mr-1"></i>File selected
          </span>
        </div>
      </div>
      <button
        type="button"
        class="btn-remove-file"
        @click.stop="clearFile"
        :disabled="disabled"
        title="Remove file"
      >
        <i class="fa fa-times-circle fa-lg"></i>
      </button>
    </div>

    <!-- Empty / Drop Prompt Presentation -->
    <div v-else class="file-dropzone-content">
      <div class="file-dropzone-icon">
        <i :class="computedDefaultIcon"></i>
      </div>

      <div class="file-dropzone-body">
        <div class="file-dropzone-title">
          <span v-if="isDragOver" class="text-primary font-weight-bold">
            <i class="fa fa-arrow-down mr-1"></i>Release to drop file here
          </span>
          <span v-else>{{ title }}</span>
        </div>

        <div v-if="effectiveSubtitle" class="file-dropzone-hint">
          {{ effectiveSubtitle }}
        </div>
      </div>

      <button
        type="button"
        class="file-dropzone-btn mt-1"
        :disabled="disabled"
        tabindex="-1"
      >
        <i class="fa fa-folder-open"></i>{{ buttonText }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "BaseFileUpload",
  props: {
    value: {
      type: [File, Object, String],
      default: null
    },
    selectedName: {
      type: String,
      default: ""
    },
    accept: {
      type: String,
      default: "*"
    },
    title: {
      type: String,
      default: "Choose a file or drag & drop it here"
    },
    subtitle: {
      type: String,
      default: ""
    },
    hint: {
      type: String,
      default: ""
    },
    icon: {
      type: String,
      default: ""
    },
    buttonText: {
      type: String,
      default: "Browse File"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isDragOver: false,
      dragCounter: 0,
      internalFile: null,
      internalFileName: ""
    };
  },
  computed: {
    inputId() {
      return this.id || `file-upload-${this._uid}`;
    },
    effectiveSubtitle() {
      if (this.subtitle) return this.subtitle;
      if (this.hint) return this.hint;
      if (this.accept && this.accept !== "*") {
        return `Supported formats: ${this.accept}`;
      }
      return "";
    },
    displayFileName() {
      if (this.internalFileName) return this.internalFileName;
      if (this.selectedName) return this.selectedName;
      if (this.value) {
        if (typeof this.value === "string") return this.value;
        if (this.value.name) return this.value.name;
      }
      return "";
    },
    displayFileSize() {
      const file = this.internalFile || (this.value instanceof File ? this.value : null);
      if (!file || !file.size) return "";
      const bytes = file.size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
    computedDefaultIcon() {
      if (this.icon) return this.icon;
      const lowerAccept = (this.accept || "").toLowerCase();
      if (lowerAccept.includes("xls") || lowerAccept.includes("csv")) {
        return "fa fa-file-excel";
      }
      if (lowerAccept.includes("image") || lowerAccept.includes("png") || lowerAccept.includes("jpg")) {
        return "fa fa-file-image";
      }
      if (lowerAccept.includes("pdf")) {
        return "fa fa-file-pdf";
      }
      return "fa fa-cloud-upload-alt";
    },
    computedFileIcon() {
      const name = (this.displayFileName || "").toLowerCase();
      if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv")) {
        return "fa fa-file-excel text-success";
      }
      if (name.endsWith(".pdf")) {
        return "fa fa-file-pdf text-danger";
      }
      if (name.endsWith(".doc") || name.endsWith(".docx")) {
        return "fa fa-file-word text-primary";
      }
      if (
        name.endsWith(".png") ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg") ||
        name.endsWith(".gif") ||
        name.endsWith(".webp")
      ) {
        return "fa fa-file-image text-info";
      }
      return "fa fa-file-alt text-primary";
    }
  },
  watch: {
    selectedName(val) {
      if (!val) {
        this.internalFileName = "";
        this.internalFile = null;
      }
    },
    value(val) {
      if (!val) {
        this.internalFile = null;
        this.internalFileName = "";
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      } else if (val instanceof File) {
        this.internalFile = val;
        this.internalFileName = val.name;
      }
    }
  },
  methods: {
    onDragEnter() {
      if (this.disabled) return;
      this.dragCounter++;
      this.isDragOver = true;
    },
    onDragOver() {
      if (this.disabled) return;
      this.isDragOver = true;
    },
    onDragLeave() {
      if (this.disabled) return;
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.isDragOver = false;
      }
    },
    onDrop(e) {
      if (this.disabled) return;
      this.dragCounter = 0;
      this.isDragOver = false;

      const droppedFiles = e.dataTransfer && e.dataTransfer.files;
      if (!droppedFiles || droppedFiles.length === 0) return;

      if (this.$refs.fileInput) {
        try {
          this.$refs.fileInput.files = droppedFiles;
        } catch (err) {
          // assignment fallback
        }
      }

      this.processFiles(droppedFiles, e);
    },
    onNativeChange(e) {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      this.processFiles(files, e);
    },
    processFiles(fileList, sourceEvent) {
      const file = fileList[0];
      this.internalFile = file;
      this.internalFileName = file ? file.name : "";

      this.$emit("input", this.multiple ? Array.from(fileList) : file);

      // Create an event object that preserves event.target.files for legacy handlers
      const eventPayload = {
        target: {
          files: fileList,
          value: this.internalFileName
        },
        file: file,
        files: fileList,
        originalEvent: sourceEvent
      };

      this.$emit("change", eventPayload);
      this.$emit("file-selected", file);
    },
    clearFile() {
      this.internalFile = null;
      this.internalFileName = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      this.$emit("input", null);
      this.$emit("clear");
      this.$emit("change", {
        target: {
          files: [],
          value: ""
        },
        file: null,
        files: []
      });
    },
    clear() {
      this.clearFile();
    }
  }
};
</script>
